import { hanoiHint } from '../../../packages/domain/src/62-progressive-idle.mjs';
import { paintIconAction } from './82-icon-actions.mjs';

/** A finite, keyboard reachable introduction. Callers retain their own pause
 * policy and clock; this view never starts an independent animation loop. */
export function createCraftBriefing(
  ctx,
  { host, title, steps, onOpen = () => {}, onClose = () => {} },
) {
  const panel = ctx.e('section', 'craft-briefing');
  panel.setAttribute('aria-label', `${title} introduction`);
  const heading = ctx.e('h3', '', title);
  const list = ctx.e('ol', 'craft-briefing-steps');
  for (const [symbol, text] of steps) {
    const row = ctx.e('li');
    const art = ctx.e('span', 'craft-step-symbol', symbol);
    art.setAttribute('aria-hidden', 'true');
    row.append(art, ctx.e('span', '', text));
    list.append(row);
  }
  const start = ctx.e('button', 'game-action craft-start', 'Let’s play');
  start.type = 'button';
  const privacy = ctx.e('small', '', 'Private play · take your time');
  panel.append(heading, list, start, privacy);
  host.append(panel);
  let disposed = false,
    opener;
  function close() {
    if (disposed || panel.hidden) return;
    panel.hidden = true;
    onClose();
    opener?.focus?.({ preventScroll: true });
  }
  start.addEventListener('click', close);
  panel.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  });
  return {
    isOpen: () => !panel.hidden && !disposed,
    open(button) {
      if (disposed) return;
      opener = button;
      panel.hidden = false;
      onOpen();
      start.focus?.({ preventScroll: true });
    },
    dispose() {
      disposed = true;
      panel.remove();
    },
  };
}

/** Arcade presentation for the private Hanoi and Brook puzzles. State,
 * scoring and Undo remain parent-owned; hints inspect only public discs. */
export function createCraftPresentation(
  ctx,
  {
    host,
    board,
    playfield,
    controls,
    arcade,
    roundLabel,
    runBest,
    feedback,
    undo,
    action,
    readState,
    move,
    allowed,
    visible = () => true,
    restore,
  },
) {
  const hanoi = readState().gameId === 'pebble-balance';
  let initial = readState(),
    disposed = false,
    generation = 0,
    timer = 0;
  const animations = new Set();
  const guide = ctx.e('div', 'craft-route-strip');
  guide.setAttribute('role', 'group');
  host.insertBefore(guide, controls);
  const toast = ctx.e('div', 'craft-event');
  toast.setAttribute('aria-hidden', 'true');
  toast.hidden = true;
  playfield.append(toast);
  const briefing = createCraftBriefing(ctx, {
    host: playfield,
    title: hanoi ? 'Build a little brilliance' : 'Bring the valley to life',
    steps: hanoi
      ? [
          ['① → ③', 'Move the whole tower to the glowing right platform.'],
          ['◇ → ◆', 'Lift one top disc; smaller discs rest on larger ones.'],
          ['↶ ✦', 'Try a sequence. Undo, Restart and Hint are always here.'],
        ]
      : [
          ['💧 →', 'Draw or tap a connected stream from the spring.'],
          ['① ② ③', 'Visit numbered flowers in order, then reach the pool.'],
          ['↶ ═', 'Step back to rethink. Cross wooden aqueducts straight.'],
        ],
    onOpen: () => {
      board.inert = true;
      cancel();
    },
    onClose: () => {
      board.inert = false;
      if (board.gameView) update(board.gameView);
      board.querySelector('button:not(:disabled)')?.focus?.({ preventScroll: true });
    },
  });
  board.inert = true;
  const restart = action(
    '',
    'Restart this puzzle',
    () => {
      if (allowed()) restore(initial);
    },
    'rotate-ccw',
  );
  const help = action('', 'How to play', () => briefing.open(help), 'circle-help');
  controls.insertBefore(restart, undo.nextSibling);
  controls.append(help);
  let hint;
  if (hanoi) {
    hint = action(
      '',
      'Hint for this tower',
      () => {
        if (!allowed() || briefing.isOpen()) return;
        if (readState().data.selected >= 0)
          move({ type: 'cell', index: readState().data.selected });
        cancel();
        const result = hanoiHint(board.gameView);
        if (result.kind !== 'move') return;
        for (const [index, role] of [
          [result.from, 'from'],
          [result.to, 'to'],
        ]) {
          const node = board.querySelector(`[data-cell="${index}"]`);
          if (node) node.dataset.craftHint = role;
        }
        feedback.textContent = `Tower ${result.from + 1} → ${result.to + 1}. ${result.remaining} moves remain on a shortest route from here.`;
      },
      'sparkles',
    );
    controls.insertBefore(hint, restart.nextSibling);
  }
  paintIconAction(ctx, undo, 'undo-2', 'Undo the last action');
  arcade.hidden = false;
  function cancel() {
    generation++;
    clearTimeout(timer);
    for (const animation of animations) animation.cancel();
    animations.clear();
    toast.hidden = true;
    for (const cell of board.children) delete cell.dataset.craftHint;
  }
  function pulse(node, title, finale = false) {
    if (
      !node ||
      document.hidden ||
      disposed ||
      !allowed() ||
      !visible() ||
      briefing.isOpen() ||
      !host.isConnected
    )
      return;
    toast.textContent = title;
    toast.dataset.finale = String(finale);
    const origin = playfield.getBoundingClientRect?.(),
      bounds = node.getBoundingClientRect?.();
    if (!origin || !bounds) return;
    toast.style.left = `${bounds.left + bounds.width / 2 - origin.left}px`;
    toast.style.top = `${bounds.top + bounds.height * 0.3 - origin.top}px`;
    toast.hidden = false;
    const token = generation;
    timer = setTimeout(
      () => {
        if (token === generation) toast.hidden = true;
      },
      finale ? 1800 : 1150,
    );
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !toast.animate) return;
    const animation = toast.animate(
      [
        { opacity: 0, transform: 'translate(-50%, 6px) scale(.85)' },
        { opacity: 1, transform: 'translate(-50%, -4px) scale(1.04)', offset: 0.15 },
        { opacity: 1, transform: 'translate(-50%, -12px) scale(1)', offset: 0.7 },
        { opacity: 0, transform: 'translate(-50%, -22px) scale(1)' },
      ],
      { duration: finale ? 1800 : 1150, easing: 'ease-out' },
    );
    animations.add(animation);
    void animation.finished.then(
      () => {
        animations.delete(animation);
        if (token === generation) toast.hidden = true;
      },
      () => {},
    );
  }
  function update(view) {
    const state = readState(),
      d = state.data;
    board.gameView = view;
    if (state.seed !== initial.seed || d.stage !== initial.data.stage) initial = state;
    roundLabel.textContent = `${hanoi ? 'TOWER' : 'VALLEY'} ${String(d.stage).padStart(2, '0')}`;
    runBest.textContent = hanoi
      ? `${d.transfers} MOVES · ${view.scene.minimum} IDEAL`
      : `${d.path.length - 1} STEPS`;
    restart.disabled = !allowed();
    if (hint) hint.disabled = !allowed() || d.stageComplete;
    help.disabled = !allowed();
    guide.replaceChildren();
    guide.setAttribute('aria-label', hanoi ? view.progress.label : 'Flower sequence');
    const count = hanoi ? d.discs : d.checkpoints.length;
    for (let i = 0; i < count; i++) {
      const done = hanoi ? d.pegs[2].includes(i + 1) : d.path.includes(d.checkpoints[i]);
      const pip = ctx.e('span', 'craft-route-pip', done ? '✓' : String(i + 1));
      pip.dataset.done = String(done);
      pip.dataset.next = String(!done && (hanoi || i === view.progress.current));
      pip.setAttribute('aria-hidden', 'true');
      guide.append(pip);
    }
    guide.append(
      ctx.e('span', 'craft-route-caption', hanoi ? 'Discs home' : 'Flower trail → pool'),
    );
    if (!allowed()) cancel();
  }
  function accepted(previous, next, flight) {
    cancel();
    const token = generation;
    if (briefing.isOpen()) return;
    const event = next.data.events?.[0];
    if (!event || previous.data.stage !== next.data.stage) return;
    const cell = board.querySelector(
      `[data-cell="${event.to ?? event.index ?? (hanoi ? 2 : next.data.columns ** 2 - 1)}"]`,
    );
    const node = hanoi ? cell?.querySelector?.(`[data-disc="${event.disc}"]`) || cell : cell;
    const completed = next.data.stageComplete;
    const message = completed
      ? hanoi
        ? `${next.data.transfers === 2 ** next.data.discs - 1 ? 'Perfect sequence!' : 'Tower complete!'}`
        : 'Valley restored!'
      : hanoi
        ? `◇ ${event.disc} → ${event.to + 1}`
        : `Flower ${next.data.checkpoints.indexOf(event.index) + 1} blooms!`;
    if (event.type !== 'transfer' && event.type !== 'bloom' && !completed) return;
    void Promise.resolve(flight?.finished).then(() => {
      if (token !== generation || readState() !== next) return;
      feedback.textContent = message;
      pulse(node, message, completed);
    });
  }
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const suspend = () => {
    if (document.hidden || media.matches) cancel();
  };
  document.addEventListener('visibilitychange', suspend);
  media.addEventListener?.('change', suspend);
  return {
    isOpen: briefing.isOpen,
    update,
    accepted,
    cancel,
    dispose() {
      disposed = true;
      cancel();
      briefing.dispose();
      document.removeEventListener('visibilitychange', suspend);
      media.removeEventListener?.('change', suspend);
      board.inert = false;
      guide.remove();
      toast.remove();
    },
  };
}
